import { addOrReplaceProduct, getContractedVolume, getFilteredProductQuantity } from '../../../utils/volumes';
import {
  CONTRACT_BUY_INTL_PREFIX,
  GENERAL_ERROR_KEY,
  MW_ASKING_NAME,
  MWH_ASKING_NAME,
  PERCENT_ASKING_NAME,
  PRODUCT_CHART_NAME,
  PRODUCT_NAME,
} from '../buy/ContractBuy';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { formatToMax2Decimals } from '../../../utils/number';
import useAppContext from '../../../hooks/context/useAppContext';

/**
 * Since there are inacuracies in counting, which is precise to 3 decimals,
 * we need to allow some flexibility when rounding would set it over the actual space.
 */
const ALLOWED_OVER_CONTRACTED_LIMIT = 0.05;

export const useFormManupilation = (quantities, setValue) => {
  const [purchaseInfo, setPurchaseInfo] = useState();
  /**
   * @param {PurchaseInfo} buyInformation purchaseInfo
   * @param {string} changedInput Input name where onchange fired
   * @returns {void}
   */
  const onPurchaseInfoChanged = useCallback((buyInformation, changedInput) => {
    setPurchaseInfo(buyInformation);
    const setFormNumberValueRoundTo2Decimals = (name, value) => setValue(name, formatToMax2Decimals(value));

    const buyTotalsInfo = buyInformation.buyTotalsInfo;
    if (changedInput !== PERCENT_ASKING_NAME) {
      setFormNumberValueRoundTo2Decimals(PERCENT_ASKING_NAME, buyTotalsInfo.percentage * 100);
    }
    if (changedInput !== MW_ASKING_NAME) {
      setFormNumberValueRoundTo2Decimals(MW_ASKING_NAME, buyTotalsInfo.volumeMW);
    }
    if (changedInput !== MWH_ASKING_NAME) {
      setFormNumberValueRoundTo2Decimals(MWH_ASKING_NAME, buyTotalsInfo.volumeMWh);
    }
    const newProducts = addOrReplaceProduct(quantities, buyInformation.buyingProduct);
    setValue(PRODUCT_CHART_NAME, newProducts);
  }, [quantities, setValue]);
  return { onPurchaseInfoChanged, purchaseInfo, setPurchaseInfo };
};

export const useValidation = (product, getValues, countMaxAllowed, getFilteredVolumes, releasing, year) => {
  const intl = useIntl();
  const [errors, setErrors] = useState({});
  const { getUser } = useAppContext();
  const validate = useCallback(() => {
    const errors = {};
    const values = getValues();
    const loggedUser = getUser();
    const purchaseConfiguration = product.purchaseConfiguration;
    if (values[MWH_ASKING_NAME] < purchaseConfiguration.minAmount) {
      errors[GENERAL_ERROR_KEY] = intl.formatMessage({
        id: `${CONTRACT_BUY_INTL_PREFIX}.error.underMinAmount`,
      }, {
        minAmount: purchaseConfiguration.minAmount,
      });
    }
    if (values[MWH_ASKING_NAME] > purchaseConfiguration.maxAmount) {
      errors[GENERAL_ERROR_KEY] = intl.formatMessage({
        id: `${CONTRACT_BUY_INTL_PREFIX}.error.overMaxAmount`,
      }, {
        maxAmount: purchaseConfiguration.maxAmount,
      });
    }
    const filteredVolumes = getFilteredVolumes(getValues[PRODUCT_CHART_NAME]);
    const maxAllowance = countMaxAllowed(filteredVolumes, year, releasing);
    if (loggedUser && loggedUser?.role !== 'ADMIN' && values[MWH_ASKING_NAME] - ALLOWED_OVER_CONTRACTED_LIMIT > maxAllowance.buyTotalsInfo.volumeMWh) {
      errors[GENERAL_ERROR_KEY] = intl.formatMessage({
        id: `${CONTRACT_BUY_INTL_PREFIX}.error.overContractedVolume`,
      });
    }

    setErrors(errors);
    return {
      success: Object.keys(errors).length === 0,
      errors,
    };
  }, [product, getValues, countMaxAllowed, getFilteredVolumes, intl, year, releasing, getUser]);
  return { validate, errors };
};

export const useSelectedProduct = (products, watch) => {
  const { findProductById } = useFindProductById(products);
  const product = findProductById(watch(PRODUCT_NAME));
  return { product };
};

export const useFindProductById = (products) => {
  const findProductById = useCallback((productId) => {
    return products && products.find((findProduct) => findProduct.id === Number(productId));
  }, [products]);
  return { findProductById };
};


export const useProductManipulation = (getValues, quantities, products) => {
  const { findProductById } = useFindProductById(products);
  const getFilteredContractedVolume = useCallback(() => {
    const productId = getValues()[PRODUCT_NAME];
    const product = findProductById(productId);
    return getFilteredProductQuantity(getContractedVolume(quantities), product);
  }, [getValues, quantities, findProductById]);

  const getFilteredVolumes = useCallback(() => {
    const productId = getValues()[PRODUCT_NAME];
    const product = findProductById(productId);
    return quantities.map((productQuantity) => getFilteredProductQuantity(productQuantity, product));
  }, [getValues, quantities, findProductById]);
  return { getFilteredContractedVolume, getFilteredVolumes };
};
